import { createPageTranslationsBackground } from '../translation/page-translations-background'
import { flashcardsReplicationBudgets } from '../db/replication-budgets'
import type { TranslateTexts } from '../translation/page-translations'
import { clearTursoWorkerDatabasesByPrefix, createTursoWorkerDriver, createReplicaTransport, ReplicaTransportError, readReplicaState } from '@w/db-turso-browser-adapter/worker'
import type { Tel } from '@w/tel'
import { startSpaceReplica } from './space-replica-startup'
import { createFlashcardsTursoWorker } from '../db/create-flashcards-turso-worker'
import { createClientDb } from '../db/db'
import { flashcardsLocalTables } from '../db/workspace-tables'
import { createPageEmbeddingsBrowserClient } from '../search/page-embeddings-browser.client'
import type { Space, SpaceFile } from './spaces-resources'
import { createServerEmbeddingsBackground } from '../search/server-embeddings-background'
import type { ServerEmbeddings } from '../search/server-embeddings.client'
import { isDeletedWorkspaceReset } from './space-generation-reset'
import type { SpaceRuntime } from './spaces-runtime'
import { flashcardsDeviceSchemaId } from '../db/client-migrations'
import { safe } from '@w/utils'
import { createReplicaTransportStatus } from './replica-transport-status'
import { receivedContent, replicaScopeColumns } from '../db/received-content-declarations'
import { exportPendingWork, importPendingWork, pendingStore, persistLocalWork, persistPendingWork } from '../db/reexpress-pending'
import { holdResetImport, releaseResetImport } from './reset-import-hold'

export type BrowserSpaceInput = { signal?: AbortSignal; space: Space; userId: string; deviceId: string; backendUrl: string; replicaFetch?: Window['fetch']; fetch?: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>; storage: Storage; tel: Tel; bootTel: Tel; serverEmbeddings?: ServerEmbeddings; translate?: TranslateTexts; language?: () => string; onTranslationsWarmed?: () => void; storageScope?: string }
const browserRuntime: SpaceRuntime = {
  path: name => name,
  createDriver: options => Promise.resolve(createTursoWorkerDriver({ ...options, createWorker: createFlashcardsTursoWorker })),
  deleteDatabase: clearTursoWorkerDatabasesByPrefix,
  async deleteContentCache(path) {
    const root = await navigator.storage.getDirectory()
    try { await root.removeEntry(path, { recursive: true }) }
    catch (error) { if (!(error instanceof DOMException) || error.name !== 'NotFoundError') throw error }
  },
  embeddings: createPageEmbeddingsBrowserClient,
}
/** A replica file retired by a reset; it is deleted only after the replica that replaced it holds its saved work. */
type RetiredReplica = { identityKey: string; identity: number[]; imported?: boolean; production?: boolean }
const nativeFileName = (identityKey: string) => `flashcards-native-v2-${encodeURIComponent(identityKey)}.db`
function readRetired(storage: Storage, key: string): RetiredReplica[] {
  const stored = storage.getItem(key)
  return stored === null ? [] : JSON.parse(stored) as RetiredReplica[]
}
function writeRetired(storage: Storage, key: string, retired: RetiredReplica[]) {
  if (retired.length === 0) storage.removeItem(key)
  else storage.setItem(key, JSON.stringify(retired))
}

/**
 * db-crdt-sync spec :37: a reset never loses work. Each retired file's saved rows (_turso_reset_pending) are written
 * into this replica in one transaction, marked imported, and only then is the file deleted. A deletion that fails
 * is retried on the next open without importing again: the rows may already be re-expressed and gone.
 */
async function adoptRetiredReplicas(input: BrowserSpaceInput, runtime: SpaceRuntime, key: string, target: ReturnType<typeof createTursoWorkerDriver>,
  config: Parameters<typeof createReplicaTransport>[0]) {
  const retired = readRetired(input.storage, key)
  for (const entry of retired.slice()) {
    const path = runtime.path(nativeFileName(entry.identityKey))
    if (!entry.imported) {
      // No failure scope: it names the fresh replica (its identity), and the retired file only reads its saved rows.
      const { failureScope: _scope, ...retained } = config
      const replica = { ...retained, identity: Uint8Array.from(entry.identity) }
      // The retired file is only read: its transport never reaches the server.
      const old = await runtime.createDriver({ path, replica, tel: input.tel,
        replicaTransport: createReplicaTransport(replica, () => Promise.reject(new TypeError('Retired replica is offline')), input.tel) })
      let rows: Record<string, unknown>[]
      // requirements.md 2026-10-27 16:5x: a file retired by a production reset holds data that waits for the user's
      // import; the hold is recorded before the rows reach the fresh replica, so they are never re-expressed unasked.
      if (entry.production) holdResetImport(input.storage, input.userId, input.space.workspaceId)
      // Work a 410 at open left unsaved (the file was refused before replace() could save it) is saved here first.
      try {
        if (entry.production) await persistLocalWork(pendingStore(old))
        await persistPendingWork(pendingStore(old)); rows = await exportPendingWork(pendingStore(old))
      } finally { await old.close?.() }
      if (entry.production && rows.length === 0) releaseResetImport(input.storage, input.userId, input.space.workspaceId)
      await importPendingWork(pendingStore(target), rows)
      entry.imported = true
      writeRetired(input.storage, key, retired)
    }
    try {
      await runtime.deleteDatabase(path)
      await runtime.deleteContentCache(`${path}.content-cache`)
      input.storage.removeItem(`${entry.identityKey}:generation`)
      input.storage.removeItem(`${entry.identityKey}:identity`)
      retired.splice(retired.indexOf(entry), 1)
      writeRetired(input.storage, key, retired)
    } catch (error) { input.tel.warn('Retired replica deletion failed; retried on the next open', { error: String(error) }) }
  }
}

export function openBrowserSpace(input: BrowserSpaceInput, runtime: SpaceRuntime = browserRuntime): Promise<SpaceFile> {
  return openBrowserSpaceAttempt(input, runtime, true)
}

async function openBrowserSpaceAttempt(input: BrowserSpaceInput, runtime: SpaceRuntime, retryConstructorReset: boolean): Promise<SpaceFile> {
  let driver: ReturnType<typeof createTursoWorkerDriver> | undefined
  // `bootTel` scopes only this one-time driver boot (e.g. under an
  // `app.startup` trace); `input.tel` (the file's long-lived tel) is what
  // every later query attaches to, so it must never be a since-ended span.
  const bootTel = input.bootTel
  let oldPath: string | undefined
  let generationKeyToClear: string | undefined
  let retainedIdentityKey: string | undefined
  let incarnationToClear: string | undefined
  async function purge() {
    if (!oldPath) return
    await runtime.deleteDatabase(oldPath)
    await runtime.deleteContentCache(`${oldPath}.content-cache`)
    const key = browserReplicaStorageKey(input)
    if (input.storage.getItem(key) === incarnationToClear) input.storage.removeItem(key)
    if (generationKeyToClear) input.storage.removeItem(generationKeyToClear)
    if (retainedIdentityKey) input.storage.removeItem(retainedIdentityKey)
  }
  let retiring: { identityKey: string; identity: Uint8Array } | undefined
  /** db-crdt-sync spec :37: a file refused for a missed freeze window or lease (410 eligibility_expired) holds unsynced
   * work; it is retired (adoptRetiredReplicas saves and carries its work into the fresh replica, then deletes it). A
   * production reset (410 production_reset) retires the file too: its data waits for the user's "Import your data"
   * (requirements.md 2026-10-27 16:5x). Any other generation reset (a readable-set change) purges the file. */
  function replaceFailedReplica(error: ReplicaTransportError): Promise<void> {
    if (error.code !== 'eligibility_expired' && error.code !== 'production_reset') return purgeFailedReplica(error)
    retireFailedReplica(error.code === 'production_reset')
    return Promise.resolve()
  }
  function retireFailedReplica(production: boolean) {
    const current = safe(retiring, 'INV: a retired replica has a computed identity')
    const key = browserReplicaStorageKey(input) + ':retired'
    writeRetired(input.storage, key, [...readRetired(input.storage, key), { identityKey: current.identityKey, identity: Array.from(current.identity), ...(production ? { production } : {}) }])
    createFreshBrowserReplica(input)
  }
  async function purgeFailedReplica(error: unknown) {
    try { await purge() }
    catch (purgeError) { throw new SuppressedError(purgeError, error, 'Failed to purge reset replica') }
  }
  try {
    const releasedKey = browserReplicaStorageKey(input, 'flashcards-native-v1')
    const releasedIncarnation = input.storage.getItem(releasedKey)
    if (releasedIncarnation !== null) {
      const releasedIdentityKey = JSON.stringify([releasedKey, releasedIncarnation])
      const releasedPath = runtime.path(`flashcards-native-v1-${encodeURIComponent(releasedIdentityKey)}.db`)
      await runtime.deleteDatabase(releasedPath)
      await runtime.deleteContentCache(`${releasedPath}.content-cache`)
      input.storage.removeItem(releasedIdentityKey + ':generation')
      input.storage.removeItem(releasedIdentityKey + ':identity')
      input.storage.removeItem(releasedKey + ':schema')
      input.storage.removeItem(releasedKey)
    }
    const incarnationKey = browserReplicaStorageKey(input)
    let storedIncarnation = input.storage.getItem(incarnationKey)
    const schemaKey = incarnationKey + ':schema'
    if (storedIncarnation !== null && input.storage.getItem(schemaKey) !== flashcardsDeviceSchemaId) {
      // Reset files before attaching the r10 local vector schema.
      const previousIdentityKey = JSON.stringify([incarnationKey, storedIncarnation])
      await runtime.deleteDatabase(runtime.path(`flashcards-native-v2-${encodeURIComponent(previousIdentityKey)}.db`))
      input.storage.removeItem(previousIdentityKey + ':generation')
      input.storage.removeItem(previousIdentityKey + ':identity')
      input.storage.removeItem(incarnationKey)
      storedIncarnation = null
    }
    const incarnation = storedIncarnation ?? crypto.randomUUID()
    incarnationToClear = incarnation
    if (storedIncarnation === null) input.storage.setItem(incarnationKey, incarnation)
    input.storage.setItem(schemaKey, flashcardsDeviceSchemaId)
    const identityKey = JSON.stringify([incarnationKey, incarnation])
    const path = runtime.path(`flashcards-native-v2-${encodeURIComponent(identityKey)}.db`)
    oldPath = path
    const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(identityKey)))
    retainedIdentityKey = identityKey+':identity'
    const retainedIdentity = input.storage.getItem(retainedIdentityKey)
    const identity = retainedIdentity === null ? digest.slice(0, 16) : Uint8Array.from(JSON.parse(retainedIdentity) as number[])
    retiring = { identityKey, identity }
    const replica = Array.from(identity, value => value.toString(16).padStart(2, '0')).join('')
    const generationKey = `${identityKey}:generation`
    generationKeyToClear = generationKey
    const previousGeneration = input.storage.getItem(generationKey)
    const base = `${input.backendUrl.replace(/\/$/, '')}/replica`
    const workspace = encodeURIComponent(input.space.workspaceId)
    let generation = previousGeneration === null ? undefined : Number(previousGeneration)
    if (generation !== undefined && (!Number.isSafeInteger(generation) || generation < 1)) throw new Error('Invalid persisted replica generation')
    if (generation === undefined) {
      const response = await (input.fetch ?? globalThis.fetch)(`${base}/admit?workspace=${workspace}`, {
        method: 'POST', signal: input.signal, credentials: 'include', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ replica }),
      })
      if (!response.ok) {
        const failure = await response.json() as { code?: string; message?: string; deleted?: boolean }
        throw new ReplicaTransportError(response.status, failure.code, failure.message, undefined, failure.deleted === true)
      }
      const admission = await response.json() as { generation: number }
      if (!Number.isSafeInteger(admission.generation) || admission.generation < 1) throw new Error('Invalid replica generation')
      input.storage.setItem(generationKey, String(admission.generation))
      generation = admission.generation
    } else {
      // A declared attachment cannot reset its catalog before re-bootstrapping
      // undeclared. Detect retained generation reset before constructing it.
      try {
        const response = await (input.fetch ?? globalThis.fetch)(`${base}/admit?workspace=${workspace}`, {
          method: 'POST', signal: input.signal, credentials: 'include', headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ replica, generation }),
        })
        if (!response.ok) {
          const failure = response.headers.get('content-type')?.includes('application/json') ? await response.json() as { code?: string; message?: string; deleted?: boolean } : undefined
          throw new ReplicaTransportError(response.status, failure?.code, failure?.message, undefined, failure?.deleted === true)
        }
      } catch (error) {
        input.signal?.throwIfAborted()
        if (retryConstructorReset && error instanceof ReplicaTransportError && error.status === 410 && !isDeletedWorkspaceReset(error)) {
          await replaceFailedReplica(error)
          retryConstructorReset = false
          const reopened = await openBrowserSpaceAttempt(input, runtime, false)
          return reopened
        }
        if (!(error instanceof TypeError || error instanceof ReplicaTransportError && (error.status === 0 || error.status === 403))) throw error
      }
    }
    input.signal?.throwIfAborted()
    const replicaConfig = { identity, requestBytes: flashcardsReplicationBudgets.requestBytes, localTables: flashcardsLocalTables, observedTables: ['pages'], receivedContent, scopeColumns: replicaScopeColumns, failureScope: { workspace: input.space.workspaceId, principal: input.userId, generation: BigInt(generation), replica: new Uint8Array(identity) }, admitUrl: `${base}/admit?workspace=${workspace}`, sendUrl: `${base}/send?workspace=${workspace}`, fetchUrl: `${base}/fetch?workspace=${workspace}`, headers: { 'x-replica-generation': String(generation) } }
    const transportStatus = createReplicaTransportStatus()
    driver = await runtime.createDriver({ path, replica: replicaConfig,
      replicaTransport: transportStatus.wrap(createReplicaTransport(replicaConfig, input.replicaFetch ?? input.fetch ?? globalThis.fetch, input.tel)),
      tel: input.tel })
    Object.assign(driver, { subscribeTransport: transportStatus.subscribeTransport })
    const connection = driver
    let syncedOnOpen = false
    try {
      syncedOnOpen = (await startSpaceReplica({ driver, userId: input.userId, tel: bootTel, signal: input.signal, generationReset: 'reopen' })).synced
    } finally {
      // Native reset may commit before its download fails; retain that identity.
      const state = await readReplicaState(connection)
      if (!state.identity.every((byte, index) => byte === identity[index])) {
        identity.set(state.identity)
        input.storage.setItem(retainedIdentityKey, JSON.stringify(Array.from(state.identity)))
        input.storage.removeItem(generationKey)
      }
    }
    await adoptRetiredReplicas(input, runtime, incarnationKey + ':retired', connection, replicaConfig)
    const cacheDir = `${path}.content-cache`
    const local = runtime.embeddings?.({ cacheDir, driver, tel: input.tel })
    const db = createClientDb(driver, input.tel)
    const server = input.serverEmbeddings ? createServerEmbeddingsBackground({ db, embeddings: input.serverEmbeddings, tel: input.tel }) : undefined
    const translations = !input.translate || !input.language ? undefined
      : createPageTranslationsBackground({ db, translate: input.translate, language: input.language,
        readResource: { db, userId: input.userId, space: { workspaceId: input.space.workspaceId }, connectionId: path },
        skipPageIds: () => [], onWarmed: input.onTranslationsWarmed, tel: input.tel })
    const embeddings = (local ?? server) && { startBackfill() { local?.startBackfill(); server?.startBackfill() },
      update: () => { local?.update(); server?.update() },
      async waitForIdle() { await Promise.all([local?.waitForIdle(), server?.waitForIdle()]) },
      close() { local?.close(); server?.close() },
    }
    const releaseEmbeddingPages = embeddings && await (driver).subscribe('SELECT id, crdt_version(data), updatedAt, deletedAt FROM pages', () => { embeddings?.update() })
    return { syncedOnOpen, path, db, replicaIdentity: identity, ownership: driver.ownership, embeddings, translations,
      createFreshReplica() { createFreshBrowserReplica(input) },
      async retireReplica() {
        // db-crdt-sync spec :37: the old file holds the work saved before the reset. It is recorded as retired and
        // deleted by the next open once the fresh replica holds those rows (adoptRetiredReplicas).
        await connection.close?.()
        const key = incarnationKey + ':retired'
        writeRetired(input.storage, key, [...readRetired(input.storage, key), { identityKey, identity: Array.from(identity) }])
        createFreshBrowserReplica(input)
      },
      async close() { releaseEmbeddingPages?.(); translations?.close(); await translations?.waitForIdle(); embeddings?.close(); await embeddings?.waitForIdle(); await connection.close?.() },
      purge,

    }
  } catch (error) {
    await driver?.close?.()
    // Catalog bootstrap can fail before construction returns a driver. No
    // native reset handle exists yet; generation reset permits a fresh file.
    if (oldPath && retryConstructorReset && error instanceof ReplicaTransportError && error.status === 410 && !isDeletedWorkspaceReset(error)) {
      await replaceFailedReplica(error)
      return openBrowserSpaceAttempt(input, runtime, false)
    }
    if (oldPath && isDeletedWorkspaceReset(error)) await purgeFailedReplica(error)
    throw error
  }
}

export function createFreshBrowserReplica(input: { userId: string; deviceId: string; space: Pick<Space, 'workspaceId'>; storage: Storage; storageScope?: string }) {
  input.storage.setItem(browserReplicaStorageKey(input), crypto.randomUUID())
}

function browserReplicaStorageKey(input: { userId: string; deviceId: string; space: Pick<Space, 'workspaceId'>; storageScope?: string }, namespace = 'flashcards-native-v2') {
  return JSON.stringify([namespace, input.userId, input.deviceId, input.space.workspaceId, input.storageScope])
}
